import React from 'react';
import { Component, FormioFormProps, ReactComponent } from '@formio/react';
import ReactDOM from 'react-dom';

/**
 * The data that is used to initialize the formio component.
 * IMPORTANT: the type has to match the name under which you register your component.
 */
export type FormioBuilderSchema = {
  type: string;
  label: string;
  key: string;
} & Record<string, unknown>;

/**
 * The information that is displayed in the FormBuilder.
 */
export type FormioBuilderInfo = {
  title: string;
  group: string;
  icon: string;
  documentation: string;
  weight: number;
  schema: Record<string, unknown>;
};
export type EhsFormioReactProps<T> = {
  value: string;
  onChange: (value: string) => void;
  label: string;
  settings: T;
  readOnly: boolean;
};

/**
 * A wrapper to make the usage of react in formio easier.
 *
 * IMPORTANT!!!!!
 * Do not forget to add the two static methods schema and builderInfo.
 *
 * Builderinfo is used to display the component in the FormBuilder and schema is then used to initialize it.
 * The type field is especially important since it is used to retrieve your registered component.
 *
 * @param T Settings that will be passed to react component
 * @param H fields that are available in the constructor
 */
export abstract class EhsFormioReactComponent<T, H> extends ReactComponent<H> {
  private ehsLabel = '';
  private component: Component<any>;
  private readOnly: boolean;

  /**
   * if noField is set to true, the additional label is not rendered
   * https://github.com/formio/formio.js/blob/master/src/components/_classes/field/Field.js
   */
  private noField = true;

  constructor(
    component: Component<H>,
    options: FormioFormProps['options'],
    data: unknown
  ) {
    super(component, options, data);
    this.ehsLabel = component.label;
    this.component = component;
    this.readOnly = options?.readOnly ?? false;
  }

  abstract extractFields(component: Component<H>): void;
  /**
   * Attaches the {@link FormioReactWrapper} that forwards value and onChange
   *
   * @param element the html element were we attach react to
   * @param ref a ref to {@link FormioReactWrapper} that handles state changes
   */
  attachReact(element: HTMLElement, ref: (val: unknown) => void): void {
    const updateValue = this.updateValue.bind(this);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const onChange = function (val: string) {
      updateValue(val, { modified: true });
      // also update `dataForSetting` with the correct value
      that.dataForSetting = val;
    }.bind(this);
    const label = this.ehsLabel;
    this.extractFields(this.component);
    const [child, settings] = this.attachEhsReact();
    ReactDOM.render(
      <FormioReactWrapper
        ref={ref}
        onChange={onChange}
        child={child}
        settings={settings}
        label={label}
        readOnly={this.readOnly}
      />,
      element
    );
  }

  /**
   * Callback ref to store a reference to the node.
   *
   * @param element - the node
   */
  setReactInstance(element: HTMLElement): void {
    super.setReactInstance(element);
    // notify formio that the value of the element should be set
    this.shouldSetValue = true;
  }

  setValue(value: string): void {
    super.setValue(value);
    // always set the `dataForSetting`, even when it's not needed
    // to ensure that `setReactInstance` can trigger `shouldSetValue` with the correct value
    this.dataForSetting = value;
  }

  /**
   * At the end of the lifetime detach react again.
   *
   * @param element the html element were we attached react to
   */
  detachReact(element: HTMLElement): void {
    ReactDOM.unmountComponentAtNode(element);
  }

  /**
   * Return your React component here that adheres to the interface.
   */
  abstract attachEhsReact(): [
    (props: EhsFormioReactProps<T>) => JSX.Element,
    T
  ];
}
type FormioReactWrapperProps<T> = {
  onChange: (value: string) => void;
  child: (props: EhsFormioReactProps<T>) => JSX.Element;
  label: string;
  settings: T;
  readOnly: boolean;
};

/**
 * A React class component that is used as a wrapper so we can use components normally and typesafe.
 *
 * It needs to have a state value so that the {@link EhsFormioReactComponent} can retrieve and set the value.
 */
class FormioReactWrapper<T> extends React.Component<
  FormioReactWrapperProps<T>,
  { value: string }
> {
  state = {
    value: ''
  };
  constructor(props: FormioReactWrapperProps<T>) {
    super(props);
    this.onChange = this.onChange.bind(this);
  }
  onChange(value: string): void {
    this.setState(prev => ({ ...prev, value: value }));
    //forward the state change to the formio component
    this.props.onChange(value);
  }
  render() {
    return (
      <div>
        <this.props.child
          value={this.state.value}
          onChange={this.onChange}
          label={this.props.label}
          settings={this.props.settings}
          readOnly={this.props.readOnly}
        />
      </div>
    );
  }
}
